import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom-v5-compat";
import { PATH_AUTH_INVALID_SESSION } from "@hopper-b2b/common-utils";
import {
  startSession,
  fetchSessionInfo,
  elevateSession,
  type PerkspotSessionInfo,
} from "./api/auth";
import { encodePathQueryValues } from "./encodePathQueryValues";

type SessionState = "idle" | "loading" | "error" | "success";
type SessionError = "invalid_session" | "start_session_failed";

export const INIT_SESSION_LINK = import.meta.env
  .VITE_PERKSPOT_INIT_SESSION_LINK;

const COMMERCE_PORTALS_PATH_PATTERN =
  /^(\/([a-zA-Z0-9-_]+))?\/(cars|stays)(\/|$)/;

interface ISessionContext {
  /** Current session information including user details and points balance */
  sessionInfo?: PerkspotSessionInfo;
  /** Current state of the session: idle, loading, error, or success */
  sessionState: SessionState;
  /** Current error state of the session, undefined if no error */
  sessionError?: SessionError;
  /** True when points are being refreshed in the background */
  isRefreshingPoints: boolean;
  /** Authenticate user with an authorization code */
  login: (
    code: string,
    successPath: string,
    options: { elevate?: boolean }
  ) => Promise<void>;
  /** Log out the current user (not yet implemented) */
  logout: () => Promise<void>;
  /** Redirect user to elevation flow for additional authentication */
  elevateUser: (successPath: string) => Promise<void>;
  /**
   * Initialize session on app startup.
   * - Sets sessionState to "loading" (blocks UI)
   * - Skips if session already exists
   * - On error: navigates to invalid session page
   * - Use in: SessionWidget on initial mount
   */
  initSession: () => Promise<void>;
  /**
   * Refresh points balance in the background.
   * - Sets isRefreshingPoints to true (shows spinner in widget)
   * - Does not block page or affect sessionState
   * - Prevents concurrent requests
   * - On error: silently fails, keeps existing session
   * - Use in: useRefreshPoints hook at key touchpoints
   */
  refreshPoints: () => Promise<void>;
}

export const SessionContext = createContext<ISessionContext | undefined>(
  undefined
);

export const useSessionContext = (): ISessionContext => {
  const sessionContext = useContext(SessionContext);

  if (sessionContext === undefined) {
    throw new Error(`user context must be used within a AuthProvider`);
  }

  return sessionContext;
};

export const SessionContextProvider = ({ children }) => {
  const navigate = useNavigate();

  const [sessionInfo, setSessionInfo] = useState<
    PerkspotSessionInfo | undefined
  >(undefined);
  const [sessionState, setSessionState] = useState<SessionState>("idle");
  const [sessionError, setSessionError] = useState<SessionError | undefined>(
    undefined
  );
  const [isRefreshingPoints, setIsRefreshingPoints] = useState(false);

  // Ref to access sessionInfo in callbacks without adding it as a dependency
  const sessionInfoRef = useRef<PerkspotSessionInfo | undefined>(undefined);
  sessionInfoRef.current = sessionInfo;

  // Ref to prevent concurrent refresh requests (can't use state for this due to async nature)
  const isRefreshingRef = useRef(false);

  const initSession = useCallback(async () => {
    if (sessionInfoRef.current != null) {
      return;
    }

    try {
      setSessionState("loading");
      const response = await fetchSessionInfo();

      if (response == null || response.error != null) {
        console.error(response);
        setSessionState("error");
        setSessionError("invalid_session");
        navigate(PATH_AUTH_INVALID_SESSION);
      } else {
        setSessionInfo(response);
        setSessionState("success");
      }
    } catch (err) {
      console.error(err);
    }
  }, [navigate]);

  const refreshPoints = useCallback(async () => {
    if (isRefreshingRef.current) {
      return;
    }

    try {
      isRefreshingRef.current = true;
      setIsRefreshingPoints(true);

      const response = await fetchSessionInfo();

      if (response != null && response.error == null) {
        setSessionInfo(response);
      }
    } catch (err) {
      console.error(err);
    } finally {
      isRefreshingRef.current = false;
      setIsRefreshingPoints(false);
    }
  }, []);

  const login = useCallback(
    async (
      code: string,
      successPath: string,
      { elevate }: { elevate?: boolean } = {}
    ) => {
      try {
        setSessionState("loading");

        let response: PerkspotSessionInfo;
        if (elevate) {
          response = await elevateSession(code);
        } else {
          response = await startSession(code);
        }

        if (response == null || response.error != null) {
          console.error(response);
          setSessionState("error");
          setSessionError("start_session_failed");
          navigate(PATH_AUTH_INVALID_SESSION, { replace: true });
        } else {
          setSessionInfo(response);
          setSessionState("success");
          // redirect_uri is decoded by URLSearchParams before it reaches login;
          // re-encode query values so opaque tokens (+, /) survive the hop.
          const redirectTarget = encodePathQueryValues(successPath);
          if (redirectTarget.match(COMMERCE_PORTALS_PATH_PATTERN) != null) {
            window.location.href = redirectTarget;
            return;
          }
          navigate(redirectTarget);
        }
      } catch (err) {
        console.error(err);
        setSessionState("error");
        setSessionError("start_session_failed");
        navigate(PATH_AUTH_INVALID_SESSION, { replace: true });
      }
    },
    [navigate]
  );

  const logout = useCallback(async () => {
    console.error("logout is not yet implemented");
  }, []);

  const elevateUser = useCallback(
    async (successPath: string) => {
      if (sessionInfo == null) {
        console.info(
          "Tried to elevate without session info, doing regular login instead"
        );
        if (INIT_SESSION_LINK != null) {
          // We cannot append `redirectUrl` here, or we will consider it was an elevation flow. This is still
          // a bit hack-ish, the elevation flow should ideally handle not having an existing session properly.
          const baseLink = INIT_SESSION_LINK;
          window.location.href = baseLink;
          return;
        } else {
          console.error("No init session link found");
          return;
        }
      }
      const { elevateLink } = sessionInfo.userDetails;
      if (!elevateLink) {
        console.error("Tried to elevate but no elevate link found");
        return;
      }
      const localPrefix =
        import.meta.env.VITE_LOCAL_ELEVATE_REDIRECT === "true" ? "$local" : "";
      const redirectPath = localPrefix + successPath;
      const link =
        elevateLink + "&redirectUrl=" + encodeURIComponent(redirectPath);
      window.location.href = link;
    },
    [sessionInfo]
  );

  return (
    <SessionContext.Provider
      value={{
        login,
        logout,
        elevateUser,
        initSession,
        refreshPoints,
        sessionInfo,
        sessionState,
        sessionError,
        isRefreshingPoints,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
};
